/**
 * Handles:
 * - Brand/Model/Generation dropdown logic
 * - Car filtering via API
 * - Pagination (next/prev)
 * - Keyboard search submit
 */

/**
 * URL of a resized variant of an allcardb image, whatever its format:
 * photo.jpg → photo-480x360.jpg, photo.PNG → photo-480x360.PNG.
 */
function imageVariant(url, size) {
    return String(url || '').replace(/(\.[a-z0-9]+)?$/i, (ext) => `-${size}${ext}`);
}

export function initCarFinder() {
    const carFinder = document.querySelector('#car_finder');
    const filterForm = document.querySelector('#filter_form');
    const filterContainer = document.querySelector('#car_finder_filter');
    const restRoot = (window.AACarFinder && window.AACarFinder.root) || '/wp-json/autorai/v1/carfinder/';

    /* -------------------- Event bindings -------------------- */
    window.addEventListener('load', init);

    function init() {
        if (carFinder) {
            const brand = carFinder.querySelector('#brand');
            const models = carFinder.querySelector('#models');
            const gens = carFinder.querySelector('#generations');
            const types = carFinder.querySelector('#types');
            const findCar = carFinder.querySelector('#findCar');

            if (brand) brand.addEventListener('change', getModels);
            if (models) models.addEventListener('change', getGens);
            if (gens) gens.addEventListener('change', getTypes);
            if (types) types.addEventListener('change', getCarPage);
            if (findCar) findCar.addEventListener('click', e => {
                e.preventDefault();
                getCarPage();
            });
        }

        if (filterContainer) {
            const searchInput = filterContainer.querySelector('input[name=search]');
            const nextPageBtn = filterContainer.querySelector('.next_page');
            const prevPageBtn = filterContainer.querySelector('.prev_page');

            if (nextPageBtn) nextPageBtn.addEventListener('click', () => nextFilteredPage());
            if (prevPageBtn) prevPageBtn.addEventListener('click', () => prevFilteredPage());
            if (searchInput) {
                searchInput.addEventListener('keydown', e => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        filterCars();
                    }
                });
            }
        }

        if (filterForm) {
            const submitBtn = filterForm.querySelector('.filter_submit');
            if (submitBtn) submitBtn.addEventListener('click', e => {
                e.preventDefault();
                filterCars();
            });
        }
    }

    /* -------------------- Filtering -------------------- */
    async function filterCars(navigating) {
        const pageInput = filterForm.querySelector('input[name=page]');
        if (!navigating) pageInput.value = 1;

        filterContainer.classList.add('loading');

        const formData = new FormData(filterForm);
        const postData = {};

        for (const [name, value] of formData.entries()) {
            const key = name.replace('[]', '');
            const input = filterForm.querySelector(`[name="${key}"]`);
            const placeholder = input?.getAttribute('placeholder');
            if (placeholder !== value && value !== '') {
                postData[key] = postData[key]
                    ? `${postData[key]}|${value}`
                    : value;
            }
        }

        try {
            const response = await fetch(restRoot + 'filter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(postData),
            });
            const data = await response.json();
            buildCarList(data);
        } catch (err) {
            console.error('Car filter error:', err);
        } finally {
            filterContainer.classList.remove('loading');
        }
    }

    /* -------------------- Build car list -------------------- */
    function buildCarList(data) {
        const carsContainer = filterContainer.querySelector('.cars');
        if (!carsContainer) return;
        carsContainer.innerHTML = '';

        Object.values(data).forEach(car => {
            if (typeof car !== 'object') return;

            const item = document.createElement('div');
            item.className = 'item';
            item.innerHTML = `
        <a href="/autozoeker/${car.brandSlug}/${car.slug}">
          <img src="${imageVariant(car.image, '480x360')}" alt="${car.name}">
        </a>
        <figcaption>
          <a href="/autozoeker/${car.brandSlug}/${car.slug}">
            <h3 class="title">${car.brandName} ${car.name}</h3>
          </a>
        </figcaption>
      `;

            // Add type links
            const types = car.types || {};
            Object.values(types).forEach(type => {
                if (typeof type !== 'object') return;
                const row = document.createElement('div');
                row.className = 'row';
                row.innerHTML = `<a href="/autozoeker/${car.brandSlug}/${car.slug}/${type.id}">${type.name}</a>`;
                item.querySelector('figcaption').appendChild(row);
            });

            carsContainer.appendChild(item);
        });

        // Update pagination visibility
        const page = parseInt(filterForm.querySelector('input[name=page]').value, 10);
        const prevBtn = filterContainer.querySelector('.prev_page');
        const nextBtn = filterContainer.querySelector('.next_page');
        if (prevBtn) prevBtn.style.display = page > 1 ? 'block' : 'none';
        if (nextBtn) nextBtn.style.display = data.length >= 12 ? 'block' : 'none';
    }

    /* -------------------- Pagination -------------------- */
    function nextFilteredPage() {
        const pageInput = filterForm.querySelector('input[name=page]');
        pageInput.value = parseInt(pageInput.value, 10) + 1;
        filterContainer.querySelector('.prev_page')?.classList.add('visible');
        filterCars(true);
    }

    function prevFilteredPage() {
        const pageInput = filterForm.querySelector('input[name=page]');
        pageInput.value = Math.max(1, parseInt(pageInput.value, 10) - 1);
        filterCars(true);
    }

    /* -------------------- Dropdown logic -------------------- */
    function getModels() {
        const brand = carFinder.querySelector('#brand');
        const brandSlug = brand?.value;

        if (!brandSlug) return;

        fetchOptions('models', { brand: brandSlug }, '#models', 'Model');
        carFinder.querySelector('#generations').disabled = true;
        carFinder.querySelector('#types').disabled = true;
        carFinder.querySelector('input[name=url]').value = brandSlug;
    }

    function getGens() {
        const model = carFinder.querySelector('#models');
        const modelSlug = model?.value;
        if (!modelSlug) return;

        fetchOptions('generations', { model: modelSlug }, '#generations', 'Generatie');
        carFinder.querySelector('#types').disabled = true;
        carFinder.querySelector('input[name=url]').value = modelSlug;
    }

    function getTypes() {
        const gen = carFinder.querySelector('#generations');
        const genSlug = gen?.value;
        if (!genSlug) return;

        fetchOptions('types', { gen: genSlug }, '#types', 'Type');
        carFinder.querySelector('input[name=url]').value = genSlug;
    }

    /* -------------------- Utility: load dropdown options -------------------- */
    async function fetchOptions(endpoint, params, targetSelector, label) {
        const target = carFinder.querySelector(targetSelector);
        if (!target) return;

        try {
            const response = await fetch(`${restRoot}${endpoint}?${new URLSearchParams(params)}`, {
                headers: { Accept: 'application/json' },
            });
            const items = await response.json();
            target.innerHTML = `<option>${label}</option>`;
            (Array.isArray(items) ? items : []).forEach(item => {
                const option = document.createElement('option');
                option.value = item.value;
                option.textContent = item.label;
                target.appendChild(option);
            });
            target.disabled = false;
        } catch (err) {
            console.error('Dropdown fetch failed:', err);
        }
    }

    /* -------------------- Redirect logic -------------------- */
    function getCarPage() {
        const type = carFinder.querySelector('#types')?.value;
        const url = carFinder.querySelector('input[name=url]')?.value;
        const target = type && type !== 'Type' ? type : url;
        if (target) window.location.href = `/autozoeker/${target}`;
    }
}
